/**
 * National services mapped to the controls (Andres, 2026-09-24: "use the controls as the starting point on
 * how National can help member firms"). Each platform or framework National offers, who can use it, and for
 * each control what it does: covers the technical part, helps with part of it, or gives guidance. The firm
 * still owns its process, its decisions and whether to adopt a service.
 *
 * Availability (Andres, 2026-09-24): Microsoft 365 E5 and the email security stack are included for firms on
 * the National Microsoft 365 tenant; Cloudflare Zero Trust, NinjaOne, KnowBe4 and 1Password are opt-in at
 * network pricing; frameworks (policies, hardening reference, incident response) are open to every firm;
 * central logging is being evaluated and never counts as coverage.
 *
 * National IT content, no firm data. Draft until the guidance reviewer (Andres Eulacio) signs it off.
 */
import { POLICY_TEMPLATES } from './policy-templates/index.js';
export const NATIONAL_CATALOGUE_STATUS = 'draft';
export const AVAILABILITY = {
    national_tenant: { label: 'Included on the National tenant', line: 'Already in place for firms on the National Microsoft 365 tenant. Firms outside it can talk to National about joining.' },
    opt_in: { label: 'Opt in, network pricing', line: 'A nationally supported vendor. The firm signs up through National at network pricing and runs it.' },
    framework: { label: 'Open to every firm', line: 'Guidance, templates and procedures from National, free to adopt and adapt.' },
    evaluating: { label: 'Being evaluated', line: 'National is looking at options. Nothing is defined yet.' },
};
export const NATIONAL_CATALOGUE = [
    {
        id: 'm365-e5',
        name: 'Microsoft 365 E5',
        kind: 'platform',
        availability: 'national_tenant',
        line: 'The National tenant with the National Intune framework: policies, procedures and delegated admin rights so each firm manages its own devices.',
        capabilities: [
            'Endpoint management with Intune: device management, Autopilot, patching, app deployment, configuration profiles',
            'Defender for Endpoint: antivirus and threat detection (EDR)',
            'Entra ID: accounts, multi-factor sign-in, Conditional Access',
            'Purview: sensitivity labels and retention',
            'Copilot 365, Teams Phone, Power BI, Visio, Project',
        ],
    },
    {
        id: 'email-security',
        name: 'Email security',
        kind: 'platform',
        availability: 'national_tenant',
        line: 'Microsoft Defender for Office 365 and Abnormal AI protect every mailbox on the National tenant. Firms outside it can join through the network’s buying power.',
        capabilities: ['Microsoft Defender for Office 365', 'Abnormal AI'],
    },
    {
        id: 'cloudflare',
        name: 'Cloudflare Zero Trust',
        kind: 'platform',
        availability: 'opt_in',
        line: 'Secure access to firm apps from anywhere, web filtering and a managed network edge.',
        capabilities: ['Zero Trust access', 'Firewall as a service', 'Secure web gateway', 'SD-WAN'],
    },
    {
        id: 'ninjaone',
        name: 'NinjaOne',
        kind: 'platform',
        availability: 'opt_in',
        line: 'Device tracking, remote management and automated patching. The firm owns its inventory and decides its tooling.',
        capabilities: ['Asset tracking', 'Remote monitoring and management', 'Remote support and device management', 'Automation'],
    },
    {
        id: 'knowbe4',
        name: 'KnowBe4',
        kind: 'platform',
        availability: 'opt_in',
        line: 'Security awareness training, phishing simulations and a button to report suspicious email.',
        capabilities: ['Security awareness training', 'Email attack simulation campaigns', 'Phish Alert reporting (add-in)'],
    },
    {
        id: '1password',
        name: '1Password',
        kind: 'platform',
        availability: 'opt_in',
        line: 'A password manager for the whole firm, with secure sharing and dark web monitoring.',
        capabilities: ['Password manager', 'Secure sharing', 'Dark web monitoring'],
    },
    {
        id: 'hardening',
        name: 'Hardening reference',
        kind: 'framework',
        availability: 'framework',
        line: 'This portal: how to put each control in place, CIS Benchmark and vendor guides, and the AI control assistant to research your own equipment.',
        capabilities: ['How to put it in place, per control', 'CIS Benchmark and vendor hardening links', 'The AI control assistant'],
    },
    {
        id: 'policies',
        name: 'Policy templates',
        kind: 'framework',
        availability: 'framework',
        line: 'Baker Tilly policy templates tied to the controls, with a policy writer that tailors them to the firm.',
        capabilities: ['Policy templates on the Baker Tilly letterhead', 'The AI policy writer'],
    },
    {
        id: 'incident',
        name: 'Incident response',
        kind: 'framework',
        availability: 'framework',
        line: 'The national incident response plan (IRP 601) and the national reporting channel.',
        capabilities: ['Cyber Security Incident Response Plan (IRP 601)', 'National incident reporting channel'],
    },
    {
        id: 'logging',
        name: 'Central logging',
        kind: 'platform',
        availability: 'evaluating',
        line: 'National is evaluating options for central security logs and alerting. Nothing is defined yet.',
        capabilities: [],
    },
];
export const COVERAGE_LEVEL = {
    covers: { label: 'Covers it', line: 'Using the service as National sets it up puts the technical part of the control in place.' },
    helps: { label: 'Helps', line: 'The service does part of the control; the firm does the rest.' },
    guidance: { label: 'Guidance', line: 'National gives the documents, templates or advice to do it.' },
    planned: { label: 'Being evaluated', line: 'Not available yet.' },
};
const MAPPED = [
    ['1.1', 'm365-e5', 'covers', 'Intune and Defender for Endpoint list every enrolled and discovered device.'],
    ['1.1', 'ninjaone', 'covers', 'NinjaOne tracks every managed device with its hardware details.'],
    ['1.2', 'm365-e5', 'helps', 'Defender finds devices nobody manages, and Conditional Access keeps them away from firm data.'],
    ['1.2', 'ninjaone', 'helps', 'Devices without the agent stand out for review.'],
    ['2.1', 'm365-e5', 'covers', 'Intune and Defender list the software installed on every managed device.'],
    ['2.1', 'ninjaone', 'covers', 'NinjaOne keeps a software list for every managed device.'],
    ['2.2', 'm365-e5', 'helps', 'Defender Vulnerability Management flags software that is out of support.'],
    ['2.2', 'ninjaone', 'helps', 'Software versions are reported, so unsupported ones stand out.'],
    ['2.3', 'm365-e5', 'helps', 'Defender flags unwanted software and Intune can remove it.'],
    ['2.3', 'ninjaone', 'helps', 'Unauthorized software can be removed remotely.'],
    ['3.1', 'm365-e5', 'helps', 'Purview sensitivity labels and retention support the process the firm writes down.'],
    ['3.2', 'm365-e5', 'helps', 'Purview data classification shows where sensitive information is kept.'],
    ['3.3', 'm365-e5', 'helps', 'SharePoint and Teams permissions with Entra groups control who can open what.'],
    ['3.3', '1password', 'helps', 'Shared vaults control who can see each password.'],
    ['3.4', 'm365-e5', 'covers', 'Purview retention policies keep and delete information on the firm’s schedule.'],
    ['3.5', 'm365-e5', 'helps', 'Intune wipe and Autopilot reset clear devices before they are reused or retired.'],
    ['3.6', 'm365-e5', 'covers', 'Intune turns on BitLocker and FileVault encryption and keeps the recovery keys.'],
    ['4.1', 'm365-e5', 'covers', 'Intune configuration profiles and security baselines apply one documented secure setup to every device.'],
    ['4.1', 'ninjaone', 'helps', 'Policies and scripts apply standard settings to managed devices.'],
    ['4.1', 'hardening', 'guidance', 'Secure setup steps and CIS Benchmark links for each technology.'],
    ['4.2', 'hardening', 'guidance', 'Vendor and CIS Benchmark hardening for firewalls, switches and Wi-Fi; the assistant researches your model.'],
    ['4.2', 'cloudflare', 'helps', 'Firewall as a service and SD-WAN move network rules to one managed setup.'],
    ['4.3', 'm365-e5', 'covers', 'Intune sets automatic screen lock on computers and phones.'],
    ['4.4', 'hardening', 'guidance', 'Server firewall settings from the CIS Benchmarks.'],
    ['4.5', 'm365-e5', 'covers', 'Intune turns on and manages the firewall on every managed device.'],
    ['4.6', 'm365-e5', 'helps', 'Devices and apps are managed through Intune and Entra, over secure and logged channels.'],
    ['4.6', 'cloudflare', 'helps', 'Zero Trust access replaces remote admin ports open to the internet.'],
    ['4.6', 'ninjaone', 'helps', 'Remote support runs over an encrypted, logged channel.'],
    ['4.7', 'm365-e5', 'helps', 'Intune and Windows LAPS manage the built-in administrator account.'],
    ['4.7', 'hardening', 'guidance', 'Default account steps per technology from the CIS Benchmarks.'],
    ['5.1', 'm365-e5', 'covers', 'Entra ID is the list of every account in the tenant, with sign-in activity.'],
    ['5.2', '1password', 'covers', 'A unique password for every account, and dark web monitoring for exposed ones.'],
    ['5.2', 'm365-e5', 'helps', 'Entra password protection blocks common and banned passwords.'],
    ['5.3', 'm365-e5', 'helps', 'Entra sign-in activity and access reviews find dormant accounts to disable.'],
    ['5.4', 'm365-e5', 'helps', 'Entra roles and Privileged Identity Management give admins separate, time-limited rights.'],
    ['6.1', 'm365-e5', 'helps', 'Entra groups and access packages support a written process for granting access.'],
    ['6.2', 'm365-e5', 'helps', 'Disabling one Entra account removes access to every connected app at once.'],
    ['6.3', 'm365-e5', 'covers', 'Entra multi-factor sign-in and Conditional Access protect Microsoft 365 and connected apps.'],
    ['6.3', 'cloudflare', 'helps', 'Zero Trust access puts multi-factor sign-in in front of other web apps.'],
    ['6.4', 'cloudflare', 'covers', 'Zero Trust access replaces the VPN and checks multi-factor sign-in and the device first.'],
    ['6.4', 'm365-e5', 'helps', 'Conditional Access requires multi-factor sign-in for remote access through Entra.'],
    ['6.5', 'm365-e5', 'covers', 'Conditional Access and Privileged Identity Management require multi-factor sign-in for every admin role.'],
    ['7.1', 'm365-e5', 'helps', 'Defender Vulnerability Management finds and ranks weaknesses on every device.'],
    ['7.2', 'm365-e5', 'helps', 'Defender remediation tasks track each fix to closure.'],
    ['7.3', 'm365-e5', 'covers', 'Windows Update rings and Autopatch in Intune patch operating systems automatically.'],
    ['7.3', 'ninjaone', 'covers', 'Automated operating system patching, with reports.'],
    ['7.4', 'ninjaone', 'covers', 'Automated patching for common third-party apps.'],
    ['7.4', 'm365-e5', 'helps', 'Intune keeps Microsoft 365 apps and packaged apps up to date.'],
    ['8.1', 'logging', 'planned', 'Central logging is being evaluated; nothing is defined yet.'],
    ['8.2', 'm365-e5', 'helps', 'Microsoft 365 and Defender keep audit logs for the tenant.'],
    ['8.2', 'logging', 'planned', 'Central collection of other systems’ logs is being evaluated.'],
    ['8.3', 'm365-e5', 'helps', 'E5 audit keeps Microsoft 365 logs for a year.'],
    ['8.3', 'logging', 'planned', 'Central log storage is being evaluated.'],
    ['9.1', 'm365-e5', 'covers', 'Intune keeps Edge and Outlook up to date and can block outdated browsers.'],
    ['9.1', 'ninjaone', 'helps', 'Browser versions are reported and can be updated remotely.'],
    ['9.2', 'cloudflare', 'covers', 'The secure web gateway filters DNS and blocks known malicious sites.'],
    ['9.2', 'm365-e5', 'helps', 'Defender network protection blocks malicious sites on managed devices.'],
    ['10.1', 'm365-e5', 'covers', 'Defender for Endpoint antivirus and threat detection on every managed device.'],
    ['10.1', 'email-security', 'helps', 'Malware and phishing in email are stopped before they reach staff.'],
    ['10.2', 'm365-e5', 'covers', 'Defender updates its signatures automatically, enforced by Intune.'],
    ['10.3', 'm365-e5', 'covers', 'Intune turns off autorun and autoplay for removable media.'],
    ['11.2', 'm365-e5', 'helps', 'OneDrive keeps users’ files in the cloud with version history.'],
    ['12.1', 'hardening', 'guidance', 'Firmware and update guidance per network vendor.'],
    ['12.1', 'cloudflare', 'helps', 'A managed network edge is kept up to date by the provider.'],
    ['14.1', 'knowbe4', 'covers', 'A ready training program with yearly courses and completion tracking.'],
    ['14.2', 'knowbe4', 'covers', 'Phishing simulations and social engineering courses.'],
    ['14.2', 'email-security', 'helps', 'Suspicious messages are flagged to staff as they arrive.'],
    ['14.3', 'knowbe4', 'covers', 'Courses on passwords and multi-factor sign-in.'],
    ['14.3', '1password', 'helps', 'Makes strong, unique passwords the easy habit.'],
    ['14.4', 'knowbe4', 'covers', 'Courses on handling client and firm information.'],
    ['14.5', 'knowbe4', 'covers', 'Courses on mistakes that expose information, like misdirected email.'],
    ['14.6', 'knowbe4', 'covers', 'Training plus the Phish Alert button to report suspicious email.'],
    ['14.7', 'knowbe4', 'helps', 'Courses on keeping devices updated and reporting when they are not.'],
    ['14.8', 'knowbe4', 'helps', 'Courses on public and home Wi-Fi.'],
    ['14.8', 'cloudflare', 'helps', 'Zero Trust access protects firm traffic on untrusted networks.'],
    ['17.1', 'incident', 'helps', 'The national plan sets out the roles the firm names.'],
    ['17.2', 'incident', 'helps', 'The national reporting channel and a contact list to fill in.'],
    ['17.3', 'incident', 'helps', 'The national reporting process the firm adopts.'],
    ['17.3', 'knowbe4', 'helps', 'The Phish Alert button gives staff one way to report suspicious email.'],
    ['E8-PATCH-APPS', 'ninjaone', 'covers', 'Automated patching for common third-party apps.'],
    ['E8-PATCH-APPS', 'm365-e5', 'helps', 'Intune keeps Microsoft 365 apps and packaged apps up to date.'],
    ['E8-PATCH-OS', 'm365-e5', 'covers', 'Windows Update rings and Autopatch patch operating systems automatically.'],
    ['E8-PATCH-OS', 'ninjaone', 'covers', 'Automated operating system patching, with reports.'],
    ['E8-OFFICE-MACROS', 'm365-e5', 'covers', 'Intune policies block Office macros from the internet.'],
    ['E8-OFFICE-MACROS', 'hardening', 'guidance', 'Macro settings from the ASD guidance and CIS Benchmarks.'],
    ['E8-APP-HARDENING', 'm365-e5', 'covers', 'Intune and Defender attack surface reduction rules harden browsers and Office.'],
    ['E8-APP-HARDENING', 'hardening', 'guidance', 'Browser and Office hardening from the CIS Benchmarks.'],
    ['E8-MFA', 'm365-e5', 'covers', 'Entra multi-factor sign-in and Conditional Access.'],
    ['E8-MFA', 'cloudflare', 'helps', 'Zero Trust access adds multi-factor sign-in to other apps.'],
    ['E8-APP-CONTROL', 'm365-e5', 'helps', 'Intune App Control for Business allows only approved apps to run.'],
];
/** Policy templates add a guidance row for every control they are tied to that no other row mentions for them. */
function policyRows() {
    const rows = [];
    for (const t of POLICY_TEMPLATES) {
        for (const c of t.controls) {
            if (!rows.some((r) => r[0] === c))
                rows.push([c, 'policies', 'guidance', `The ${t.title} template covers this control.`]);
        }
    }
    return rows;
}
export const SERVICE_COVERAGE = [...MAPPED, ...policyRows()].map(([controlId, serviceId, level, how]) => ({ controlId, serviceId, level, how }));
const LEVEL_ORDER = ['covers', 'helps', 'guidance', 'planned'];
/** What National offers for one control, strongest first. */
export function servicesForControl(controlId) {
    return SERVICE_COVERAGE.filter((c) => c.controlId === controlId)
        .sort((a, b) => LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level))
        .map((c) => ({ ...c, service: NATIONAL_CATALOGUE.find((s) => s.id === c.serviceId) }));
}
/** The strongest level National offers for a control, or null. */
export function strongestLevel(controlId) {
    return servicesForControl(controlId)[0]?.level ?? null;
}
/** Does a service (or any service) do something real for this control: covers or helps, not guidance or planned? */
export const isHandsOn = (level) => level === 'covers' || level === 'helps';
/** Controls a service does something for, by level. */
export function controlsForService(serviceId) {
    return SERVICE_COVERAGE.filter((c) => c.serviceId === serviceId);
}
//# sourceMappingURL=national-services.js.map